import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { Tabs } from "../components/Tabs";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type Param = { id: number; concepto: string; valor: number; unidad: string; activo: boolean };
type Lote = { id: number; codigoLote: string; estado: string; costeado: boolean; kgObtenidos: number; recepcion: { especieNombre: string } };

export function Costeo() {
  const { tienePermiso } = useAuth();
  const puedeEditar = tienePermiso("costeo:editar");
  const puedeCalcular = tienePermiso("costeo:calcular");

  const [tab, setTab] = useState("calcular");
  const [manoObra, setManoObra] = useState<Param[]>([]);
  const [cif, setCif] = useState<Param[]>([]);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [mo, c, l] = await Promise.all([
        api.get("/costeo/parametros-mano-obra"),
        api.get("/costeo/parametros-cif"),
        api.get("/produccion/lotes"),
      ]);
      setManoObra(mo);
      setCif(c);
      setLotes(l);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar costeo.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  return (
    <Shell titulo="Costeo" breadcrumb="Costeo">
      <p className="text-sm text-textmuted mb-4">
        Motor de costeo: toma el costo de materia prima de la recepción de origen, suma mano de obra y CIF según los
        parámetros configurados, y distribuye el costo total entre los productos obtenidos en proporción a su peso.
      </p>
      <Tabs
        tabs={[{ key: "calcular", label: "Calcular por lote" }, { key: "parametros", label: "Parámetros de costeo" }]}
        activo={tab} onChange={setTab}
      />
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : tab === "calcular" ? (
        <TabCalcular lotes={lotes} puedeCalcular={puedeCalcular} recargar={cargar} />
      ) : (
        <TabParametros manoObra={manoObra} cif={cif} puedeEditar={puedeEditar} recargar={cargar} />
      )}
    </Shell>
  );
}

function TabCalcular({ lotes, puedeCalcular, recargar }: { lotes: Lote[]; puedeCalcular: boolean; recargar: () => void }) {
  const [loteId, setLoteId] = useState<number | null>(null);
  const [costeo, setCosteo] = useState<any>(null);
  const [margenPct, setMargenPct] = useState("35");
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calculando, setCalculando] = useState(false);

  const verCosteo = async (id: number) => {
    setLoteId(id);
    setCosteo(null);
    setError(null);
    setCargandoDetalle(true);
    try {
      setCosteo(await api.get(`/costeo/lotes/${id}`));
    } catch (e) {
      if (e instanceof ApiError && e.code === "sin_costeo") {
        setCosteo(null);
      } else {
        setError(e instanceof ApiError ? e.message : "No se pudo cargar el costeo.");
      }
    } finally {
      setCargandoDetalle(false);
    }
  };

  const calcular = async () => {
    if (!loteId) return;
    setCalculando(true);
    setError(null);
    try {
      const resultado = await api.post(`/costeo/lotes/${loteId}/calcular`, { margenPct: Number(margenPct) });
      setCosteo(resultado);
      recargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo calcular el costeo.");
    } finally {
      setCalculando(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="card overflow-hidden lg:col-span-1">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Lote</th><th className="table-th">Costeo</th></tr></thead>
          <tbody>
            {lotes.map((l) => (
              <tr key={l.id} className={`border-b border-borderc last:border-0 cursor-pointer ${loteId === l.id ? "bg-accent-light" : "hover:bg-slate-50"}`} onClick={() => verCosteo(l.id)}>
                <td className="table-td">
                  <div className="font-mono text-xs font-semibold">{l.codigoLote}</div>
                  <div className="text-xs text-textmuted">{l.recepcion.especieNombre} · {l.kgObtenidos} kg</div>
                </td>
                <td className="table-td">
                  <span className={`badge ${l.costeado ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>
                    {l.costeado ? "calculado" : "pendiente"}
                  </span>
                </td>
              </tr>
            ))}
            {lotes.length === 0 && <tr><td colSpan={2} className="table-td text-center text-textmuted py-8">Sin lotes de producción.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="lg:col-span-2">
        {!loteId ? (
          <div className="card p-6 text-sm text-textmuted">Seleccione un lote para ver o calcular su costeo.</div>
        ) : (
          <div className="card p-5">
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
            {puedeCalcular && (
              <div className="flex items-end gap-2 mb-4 border-b border-borderc pb-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Margen de utilidad (%)</label>
                  <input className="input-field w-32" type="number" step="0.1" value={margenPct} onChange={(e) => setMargenPct(e.target.value)} />
                </div>
                <button className="btn-primary" onClick={calcular} disabled={calculando}>
                  {calculando ? "Calculando..." : costeo ? "Recalcular" : "Calcular costeo"}
                </button>
              </div>
            )}
            {cargandoDetalle ? (
              <p className="text-sm text-textmuted">Cargando...</p>
            ) : !costeo ? (
              <p className="text-sm text-textmuted">Este lote todavía no tiene un costeo calculado.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  <StatTile label="Materia prima" valor={costeo.costoMateriaPrima} />
                  <StatTile label="Mano de obra" valor={costeo.costoManoObra} />
                  <StatTile label="CIF" valor={costeo.costoIndirectos} />
                  <StatTile label="Costo total" valor={costeo.costoTotal} destacado />
                </div>
                <p className="text-sm text-slate-700 mb-4">
                  Costo promedio: <span className="font-semibold">${Math.round(costeo.costoKgPromedio).toLocaleString()}/kg</span> sobre {costeo.kgTotalesObtenidos} kg totales.
                </p>
                <table className="w-full">
                  <thead><tr className="border-b border-borderc">
                    <th className="table-th">Producto</th><th className="table-th">Kg</th><th className="table-th">Costo asignado</th>
                    <th className="table-th">Margen</th><th className="table-th">Precio sugerido/kg</th>
                  </tr></thead>
                  <tbody>
                    {costeo.productos.map((p: any) => (
                      <tr key={p.id} className="border-b border-borderc last:border-0">
                        <td className="table-td font-medium">{p.productoObtenido}</td>
                        <td className="table-td">{p.kgObtenidos}</td>
                        <td className="table-td">${Math.round(p.costoAsignado).toLocaleString()}</td>
                        <td className="table-td">{p.margenPct}%</td>
                        <td className="table-td font-semibold text-accent-dark">${Math.round(p.precioSugerido).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, valor, destacado }: { label: string; valor: number; destacado?: boolean }) {
  return (
    <div className={`rounded-md p-3 border ${destacado ? "bg-navy text-white border-navy" : "bg-surface border-borderc"}`}>
      <div className={`text-[11px] ${destacado ? "text-slate-300" : "text-textmuted"}`}>{label}</div>
      <div className="text-base font-bold mt-0.5">${Math.round(valor).toLocaleString()}</div>
    </div>
  );
}

function TabParametros({ manoObra, cif, puedeEditar, recargar }: { manoObra: Param[]; cif: Param[]; puedeEditar: boolean; recargar: () => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <ParamList
        titulo="Mano de obra"
        params={manoObra}
        endpoint="/costeo/parametros-mano-obra"
        campoValor="valor"
        unidades={["hora", "mes", "kg", "lote"]}
        puedeEditar={puedeEditar}
        recargar={recargar}
      />
      <ParamList
        titulo="Costos indirectos de fabricación (CIF)"
        params={cif}
        endpoint="/costeo/parametros-cif"
        campoValor="valorUnitario"
        unidades={["kg", "lote", "mes"]}
        puedeEditar={puedeEditar}
        recargar={recargar}
      />
    </div>
  );
}

function ParamList({ titulo, params, endpoint, unidades, puedeEditar, recargar }: {
  titulo: string; params: Param[]; endpoint: string; campoValor: string; unidades: string[]; puedeEditar: boolean; recargar: () => void;
}) {
  const [concepto, setConcepto] = useState("");
  const [valor, setValor] = useState("");
  const [unidad, setUnidad] = useState(unidades[0]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const agregar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post(endpoint, { concepto, valor: Number(valor), unidad });
      setConcepto(""); setValor("");
      recargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo agregar el parámetro.");
    } finally {
      setEnviando(false);
    }
  };

  const eliminar = async (id: number) => {
    try {
      await api.del(`${endpoint}/${id}`);
      recargar();
    } catch { /* noop */ }
  };

  return (
    <div className="card p-5">
      <h3 className="text-sm font-semibold text-slate-800 mb-3">{titulo}</h3>
      <p className="text-[11px] text-textmuted mb-3">
        Solo las unidades <span className="font-mono">kg</span> y <span className="font-mono">lote</span> se suman
        automáticamente al calcular un costeo; <span className="font-mono">hora</span> y <span className="font-mono">mes</span> quedan como referencia.
      </p>
      <ul className="space-y-2 mb-4">
        {params.map((p) => (
          <li key={p.id} className="flex items-center justify-between text-sm border-b border-borderc pb-2 last:border-0">
            <span>{p.concepto} <span className="badge bg-slate-100 text-slate-500 text-[10px] ml-1">{p.unidad}</span></span>
            <span className="flex items-center gap-2">
              <span className="font-semibold">${p.valor.toLocaleString()}</span>
              {puedeEditar && <button className="text-red-600 hover:underline text-xs" onClick={() => eliminar(p.id)}>Eliminar</button>}
            </span>
          </li>
        ))}
        {params.length === 0 && <li className="text-sm text-textmuted">Sin parámetros configurados.</li>}
      </ul>
      {puedeEditar && (
        <form onSubmit={agregar} className="grid grid-cols-4 gap-2 border-t border-borderc pt-3">
          <input className="input-field col-span-2" placeholder="Concepto" value={concepto} onChange={(e) => setConcepto(e.target.value)} required />
          <input className="input-field" type="number" step="0.01" placeholder="Valor" value={valor} onChange={(e) => setValor(e.target.value)} required />
          <select className="input-field" value={unidad} onChange={(e) => setUnidad(e.target.value)}>
            {unidades.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          {error && <div className="col-span-4 text-xs text-red-700">{error}</div>}
          <button className="btn-secondary text-xs col-span-4" disabled={enviando}>+ Agregar parámetro</button>
        </form>
      )}
    </div>
  );
}
